/**
 * 27. Bank Reconciliation — statement upload; match suggestions;
 * matched/unmatched split view; partial match/FX handling; post
 * reconciliation.
 *
 * Arabic and English (C7): every word from the catalogs.
 *
 * Statement import and auto-match are roadmap F19, after the launch, so
 * Upload statement is shown disabled with that reason. The page used to show
 * a "why is posting blocked" panel counting 4 unmatched lines whatever the
 * statements said: a fixed number, not the API's, so it was removed with the
 * dead button (UI-HONEST).
 */
import {
  Card,
  PageHeader,
  DataTable,
  Skeleton,
  StatusBadge,
} from "@/components/ui";
import { PendingButton } from "@/components/PendingAction";
import { useApiList } from "@/lib/api/hooks";
import { useTranslation } from "@/lib/i18n";
import { listTitle } from "@/lib/i18n/format";
import { formatMoney, formatDate } from "@/lib/money";

interface StatementRow {
  id: string;
  statementNumber: string;
  bankAccountName: string;
  statementDate: string;
  status: "Uploaded" | "Matched" | "Posted";
  totalMatched: string;
  totalUnmatched: string;
  currencyCode: string;
}

export function BankReconciliationPage() {
  const { t } = useTranslation();
  const { data, isLoading } = useApiList<StatementRow>(
    ["finance", "bank-reconciliation"],
    "/finance/bank-reconciliation",
  );

  return (
    <div>
      <PageHeader
        title={t("finance.bankRec.title")}
        subtitle={t("finance.bankRec.subtitle")}
        actions={
          <PendingButton variant="primary" reason="afterLaunch" slice="F19">
            {t("finance.bankRec.upload")}
          </PendingButton>
        }
      />
      {isLoading || !data ? (
        <Skeleton rows={8} />
      ) : (
        <Card title={listTitle(t("finance.bankRec.list"), data.total)}>
          <DataTable<StatementRow>
            rows={data.items}
            columns={[
              {
                key: "statementNumber",
                label: t("finance.bankRec.statement"),
                render: (r) => (
                  <span className="atlas-mono">{r.statementNumber}</span>
                ),
              },
              {
                key: "bankAccountName",
                label: t("finance.bankRec.bankAccount"),
              },
              {
                key: "statementDate",
                label: t("fields.date"),
                render: (r) => formatDate(r.statementDate),
              },
              {
                key: "totalMatched",
                label: t("finance.bankRec.matched"),
                numeric: true,
                render: (r) => formatMoney(r.totalMatched, r.currencyCode),
              },
              {
                key: "totalUnmatched",
                label: t("finance.bankRec.unmatchedColumn"),
                numeric: true,
                render: (r) => formatMoney(r.totalUnmatched, r.currencyCode),
              },
              {
                key: "status",
                label: t("fields.status"),
                render: (r) => (
                  <StatusBadge status={r.status} type="BankStatement" />
                ),
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
}
